import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Leaderboard.module.css';
import GlobalHeader from '../GlobalHeader';
import AdSenseLayout from '../AdSense/AdSenseLayout';
import { AuthContext } from '../Providers/AuthContext';
import { useTheme } from '../Providers/ThemeContext';
import {
  getLeaderboardWithUserRank,
  getRegionDisplayName,
  getTimeControlDisplayName,
  LeaderboardEntry,
  LeaderboardResult,
} from '../../services/firebase/leaderboardService';
import { Region, TimeControlType } from '../../services/MMR/types';
import { playUISound } from '../../utils/UISoundEffects';

const TIME_CONTROLS: TimeControlType[] = ['sprint', 'standard', 'marathon'];
const REGIONS: (Region | 'ALL')[] = ['ALL', 'NA', 'SA', 'EU', 'AF', 'AS', 'OC'];

function Leaderboard() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { user } = useContext(AuthContext);

  // Filter state
  const [timeControl, setTimeControl] = useState<TimeControlType>('standard');
  const [region, setRegion] = useState<Region | 'ALL'>('ALL');

  // Data state
  const [leaderboardData, setLeaderboardData] = useState<LeaderboardResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch leaderboard data when filters change
  useEffect(() => {
    const fetchLeaderboard = async () => {
      setLoading(true);
      setError(null);

      try {
        const result = await getLeaderboardWithUserRank(
          timeControl,
          region,
          user?.uid
        );
        setLeaderboardData(result);
      } catch (err) {
        setError('Failed to load leaderboard. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchLeaderboard();
  }, [timeControl, region, user?.uid]);

  // Check if current user is in top 20
  const userInTop = leaderboardData?.entries.some(
    (entry) => entry.odiumI === user?.uid
  );

  const renderMedal = (rank: number) => {
    if (rank === 1) return <span className={styles.medal}>🥇</span>;
    if (rank === 2) return <span className={styles.medal}>🥈</span>;
    if (rank === 3) return <span className={styles.medal}>🥉</span>;
    return rank;
  };

  return (
    <AdSenseLayout>
      <div className={`${styles.container} ${theme === 'dark' ? styles.dark : ''}`}>
        <GlobalHeader />

        <button className={styles.homeBackButton} onClick={() => { playUISound(); navigate('/main'); }} aria-label="Back to Main">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
        </button>

        <div className={styles.content}>
          <h1 className={styles.title}>Leaderboard</h1>

          {/* Filters Section */}
          <div className={styles.filters}>
            {/* Time Control Filter */}
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Time Control</label>
              <div className={styles.filterButtons}>
                {TIME_CONTROLS.map((tc) => (
                  <button
                    key={tc}
                    className={`${styles.filterButton} ${timeControl === tc ? styles.active : ''}`}
                    onClick={() => setTimeControl(tc)}
                  >
                    {getTimeControlDisplayName(tc)}
                  </button>
                ))}
              </div>
            </div>

            {/* Region Filter */}
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Region</label>
              <select
                className={styles.regionSelect}
                value={region}
                onChange={(e) => setRegion(e.target.value as Region | 'ALL')}
              >
                {REGIONS.map((r) => (
                  <option key={r} value={r}>
                    {getRegionDisplayName(r)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className={styles.loading}>
              <div className={styles.spinner}></div>
              <span>Loading leaderboard...</span>
            </div>
          )}

          {/* Error State */}
          {error && <div className={styles.error}>{error}</div>}

          {/* Leaderboard Table */}
          {!loading && !error && leaderboardData && (
            <>
              {leaderboardData.entries.length > 0 ? (
                <div className={styles.tableContainer}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th className={styles.rankColumn}>Rank</th>
                        <th className={styles.playerColumn}>Player</th>
                        <th className={styles.ratingColumn}>Rating</th>
                        <th className={styles.gamesColumn}>Games</th>
                        <th className={styles.peakColumn}>Peak</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leaderboardData.entries.map((entry) => (
                        <tr
                          key={entry.odiumI}
                          className={`${styles.row} ${entry.odiumI === user?.uid ? styles.currentUser : ''}`}
                        >
                          <td className={styles.rank}>{renderMedal(entry.rank)}</td>
                          <td className={styles.player}>
                            <span className={styles.username}>{entry.username}</span>
                            <span className={styles.regionBadge}>{entry.region}</span>
                          </td>
                          <td className={styles.rating}>{entry.rating}</td>
                          <td className={styles.games}>{entry.games}</td>
                          <td className={styles.peak}>{entry.peak}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className={styles.emptyState}>
                  No players found in {region === 'ALL' ? 'any region' : getRegionDisplayName(region)} for {getTimeControlDisplayName(timeControl)}.
                </div>
              )}

              {/* User's Rank (if not in top 50) */}
              {!userInTop && leaderboardData.userRank && (
                <div className={styles.userRankSection}>
                  <div className={styles.userRankDivider}>
                    <span>Your Rank</span>
                  </div>
                  <div className={styles.tableContainer}>
                    <table className={styles.table}>
                      <thead>
                        <tr>
                          <th className={styles.rankColumn}>Rank</th>
                          <th className={styles.playerColumn}>Player</th>
                          <th className={styles.ratingColumn}>Rating</th>
                          <th className={styles.gamesColumn}>Games</th>
                          <th className={styles.peakColumn}>Peak</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className={`${styles.row} ${styles.currentUser}`}>
                          <td className={styles.rank}>#{leaderboardData.userRank.rank}</td>
                          <td className={styles.player}>
                            <span className={styles.username}>{leaderboardData.userRank.username}</span>
                            <span className={styles.regionBadge}>{leaderboardData.userRank.region}</span>
                          </td>
                          <td className={styles.rating}>{leaderboardData.userRank.rating}</td>
                          <td className={styles.games}>{leaderboardData.userRank.games}</td>
                          <td className={styles.peak}>{leaderboardData.userRank.peak}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Not Placed Message */}
              {!userInTop && !leaderboardData.userRank && user && (
                <div className={styles.notPlacedMessage}>
                  Complete placement games in {getTimeControlDisplayName(timeControl)} to appear on the leaderboard.
                </div>
              )}
            </>
          )}

        </div>
      </div>
    </AdSenseLayout>
  );
}

export default Leaderboard;
